import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Button } from '@platform/ui';
import { SEOUL_TZ } from '@platform/datetime';
import { isPerpetualSegment, specialSegmentLabel } from '@platform/billing';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import {
  useAnnualTerminationPreview,
  useMyActiveLicenses,
  useMyActiveSubscription,
  useWithdrawalPreview,
} from '../api/use-subscription.query';
import { useBillingActions } from '../hooks/useBillingActions';
import { useSubscriptionLifecycle } from '../hooks/useSubscriptionLifecycle';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import { WithdrawalDialog } from './WithdrawalDialog';
import { AnnualTerminationDialog } from './AnnualTerminationDialog';
import type { LicenseStatus, MyLicense } from '../types/subscription.type';

/** 청약철회 창(일). 서버 WITHDRAWAL_WINDOW_DAYS 와 같은 값. */
const WITHDRAWAL_WINDOW_DAYS = 7;
const MS_PER_DAY = 86_400_000;

/**
 * 프리뷰를 조회할 가치가 있는가 — 서버 판정을 대신하지 않는다.
 *
 * 모든 구독 카드가 프리뷰를 던지면 대상이 아닌 구독까지 매번 서버를 때린다.
 * 여기 판정이 관대해도(경계에서 하루 넘겨 호출) 서버가 최종 판정을 하므로 안전하다.
 */
function mayWithdraw(startedAt: string, status: string): boolean {
  if (status === 'CANCELED' || status === 'EXPIRED') return false;
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return false;
  return Math.floor((Date.now() - started) / MS_PER_DAY) <= WITHDRAWAL_WINDOW_DAYS;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const DATE_FMT = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  dateStyle: 'medium',
});
function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : DATE_FMT.format(d);
}

const LICENSE_STATUS_LABEL: Record<LicenseStatus, string> = {
  ACTIVE: '사용 중',
  EXPIRED: '만료',
  REVOKED: '회수됨',
  SUSPENDED: '일시중단',
};

/** 개인 구독 상태 배지(표시 라벨 + 색상). PAST_DUE·PAUSED 는 복구 유도. */
const SUB_STATUS_META: Record<string, { label: string; className: string }> = {
  ACTIVE: { label: '이용 중', className: 'bg-emerald-50 text-emerald-700' },
  PAST_DUE: { label: '결제 지연', className: 'bg-amber-50 text-amber-700' },
  PAUSED: { label: '일시정지', className: 'bg-red-50 text-red-700' },
  CANCEL_REQUESTED: {
    label: '해지 심사 중',
    className: 'bg-amber-50 text-amber-700',
  },
  SCHEDULED_CANCEL: {
    label: '해지 예정',
    className: 'bg-amber-50 text-amber-700',
  },
  LEGACY: {
    label: '구버전 이관 라이선스',
    className: 'bg-neutral-100 text-neutral-700',
  },
  LEGACY_PERPETUAL: {
    label: '구버전 영구 라이선스',
    className: 'bg-neutral-100 text-neutral-700',
  },
};

const DATE_MD = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  month: 'long',
  day: 'numeric',
});

/**
 * 구독·라이선스 조회 모달 (아바타 메뉴 진입).
 *
 * 원칙: "내가 개인적으로 사용할 수 있는 것"만 보여준다.
 *   - 개인 라이선스: 내가 소유한 INDIVIDUAL 라이선스
 *   - 기업 라이선스: 조직에서 *나에게 배정된* BUSINESS 라이선스 (조직별 구분)
 *   - 구독 요약: 개인(INDIVIDUAL) 구독만. 기업 구독/구매·시트 관리는 기업 콘솔 소관.
 * (서버 /licenses/me/active 가 이미 authorizedUserId 기준으로 필터해 내려준다)
 */
export function SubscriptionLicenseModal({ open, onClose }: Props) {
  const sub = useMyActiveSubscription(open);
  const lic = useMyActiveLicenses(open);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [annualOpen, setAnnualOpen] = useState(false);

  const loading = sub.isLoading || lic.isLoading;
  const licenses = useMemo(() => lic.data ?? [], [lic.data]);
  const { changeCard } = useBillingActions();
  // 구독해지 취소(철회) — 확정 전(주기말 예약/승인 대기)까지 되돌린다.
  const {
    busy: lifecycleBusy,
    revokeCancellation,
    requestWithdrawal,
    requestAnnualTermination,
  } = useSubscriptionLifecycle();

  // 개인 구독만 노출 (기업 구독은 기업 콘솔 소관).
  const personalSubscription =
    sub.data && sub.data.userType === 'INDIVIDUAL' ? sub.data : null;
  const isCouponGrant = personalSubscription?.isCouponGrant === true;
  // 이관 구독은 서버가 모든 변경을 거절한다 — 해지 예약(canceledAt)도 '해지 예정'으로 보이지 않게 한다.
  const isLegacy = personalSubscription?.isLegacy === true;
  const isLegacyPerpetual = personalSubscription?.isLegacyPerpetual === true;

  // 청약철회(§6.1) — 7일 창 안의 개인 구독만 프리뷰를 조회한다.
  const withdrawalCandidate =
    personalSubscription &&
    !isCouponGrant &&
    !isLegacy &&
    mayWithdraw(personalSubscription.startedAt, personalSubscription.status)
      ? personalSubscription
      : null;
  const { data: withdrawal } = useWithdrawalPreview(
    withdrawalCandidate?.id ?? null,
    open,
  );
  // 가능하거나, 활성화로 막힌 경우에만 버튼을 보인다(사유 안내 목적).
  const showWithdrawalButton =
    withdrawal?.eligible === true || withdrawal?.reason === 'ALREADY_USED';

  // 개인 연간 중도해지(정책 제5조 ④) — 대상 여부는 서버 프리뷰가 정한다(월간·쿠폰·구 서비스 결제는 제외).
  // 심사 중(CANCEL_REQUESTED)에도 조회해 신청 시각 기준 환불 예정액을 보여 준다.
  const annualCandidate =
    personalSubscription &&
    !isCouponGrant &&
    !isLegacy &&
    (personalSubscription.status === 'ACTIVE' ||
      personalSubscription.status === 'CANCEL_REQUESTED')
      ? personalSubscription
      : null;
  const { data: annual } = useAnnualTerminationPreview(
    annualCandidate?.id ?? null,
    open,
  );
  const showAnnualButton =
    annual?.eligible === true && personalSubscription?.status === 'ACTIVE';

  const personalLicenses = useMemo(
    () => licenses.filter((l) => l.licenseType === 'INDIVIDUAL'),
    [licenses],
  );
  const enterpriseLicenses = useMemo(
    () => licenses.filter((l) => l.licenseType === 'BUSINESS'),
    [licenses],
  );

  // 기업 라이선스는 조직별로 묶어서 표시.
  const enterpriseByOrg = useMemo(() => {
    const groups = new Map<string, { name: string; items: MyLicense[] }>();
    for (const l of enterpriseLicenses) {
      const key = l.org?.id ?? l.orgId ?? 'unknown';
      const name = l.org?.name ?? '기업 라이선스';
      if (!groups.has(key)) groups.set(key, { name, items: [] });
      groups.get(key)!.items.push(l);
    }
    return [...groups.values()];
  }, [enterpriseLicenses]);

  const isEmpty =
    !loading && !personalSubscription && licenses.length === 0;

  const copyKey = async (license: MyLicense) => {
    if (!license.licenseKey) return; // 미발급 좌석 — 복사 버튼 자체가 노출되지 않음
    try {
      await navigator.clipboard.writeText(license.licenseKey);
      setCopiedId(license.id);
      setTimeout(() => setCopiedId((c) => (c === license.id ? null : c)), 1500);
    } catch {
      /* 클립보드 미지원 환경 — 무시 */
    }
  };

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title="구독·라이선스"
      size="lg"
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            닫기
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="py-10 text-center text-sm text-adobe-muted">
          불러오는 중…
        </div>
      ) : isEmpty ? (
        <div className="py-10 text-center">
          <p className="text-sm text-adobe-muted">
            사용 중인 구독·라이선스가 없습니다.
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Link to={PATHS.COUPON} onClick={onClose}>
              <Button variant="secondary" size="sm">
                쿠폰 등록
              </Button>
            </Link>
            <Link to={PATHS.STORE} onClick={onClose}>
              <Button variant="primary" size="sm">
                구독하기
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-xs text-adobe-muted">
            기업 라이선스는{' '}
            <Link
              to={PATHS.BUSINESS}
              onClick={onClose}
              className="font-medium underline"
            >
              기업 콘솔
            </Link>
            에서 배정받을 수 있어요.
          </p>
        </div>
      ) : (
        <div className="space-y-6 text-sm">
          {/* 개인 구독 요약 */}
          {personalSubscription && (
            <section>
              <SectionTitle>구독</SectionTitle>
              {(() => {
                // 해지 진행 상태 판정:
                //  - CANCEL_REQUESTED: 약정 중도해지 심사 대기
                //  - ACTIVE + canceledAt: 무약정 주기말 해지 예약(주기말까지 이용 유지)
                const pendingReview =
                  !isCouponGrant &&
                  personalSubscription.status === 'CANCEL_REQUESTED';
                // 심사 중인 건이 연간 중도해지(환불)인가 — 약정 해지와 기한·결과가 다르다.
                const annualPending = pendingReview && annual?.eligible === true;
                const scheduledCancel =
                  !isCouponGrant &&
                  !isLegacy &&
                  personalSubscription.status === 'ACTIVE' &&
                  personalSubscription.canceledAt != null;
                const cancelPending = pendingReview || scheduledCancel;
                const pausedByOrg =
                  personalSubscription.status === 'PAUSED' &&
                  personalSubscription.pausedReason === 'ORG_SUSPENDED';
                const meta = isLegacy
                  ? isLegacyPerpetual
                    ? SUB_STATUS_META.LEGACY_PERPETUAL
                    : SUB_STATUS_META.LEGACY
                  : pendingReview
                    ? SUB_STATUS_META.CANCEL_REQUESTED
                    : scheduledCancel
                      ? SUB_STATUS_META.SCHEDULED_CANCEL
                      : (SUB_STATUS_META[personalSubscription.status] ??
                        SUB_STATUS_META.ACTIVE);
                // 조직 정지는 사용자가 풀 수 없으므로 '재활성화'를 노출하지 않는다.
                const needsRecovery =
                  personalSubscription.status === 'PAST_DUE' ||
                  (personalSubscription.status === 'PAUSED' && !pausedByOrg);
                return (
                  <div className="rounded-xl border border-black/[0.08] dark:border-white/10 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-neutral-950 dark:text-neutral-100">
                        {personalSubscription.plan?.name ?? '구독'}
                      </span>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${meta.className}`}
                      >
                        {meta.label}
                      </span>
                    </div>
                    <dl className="mt-3 grid grid-cols-[5rem_1fr] gap-y-1.5">
                      <dt className="text-adobe-muted">유형</dt>
                      <dd className="text-neutral-950 dark:text-neutral-100">
                        개인
                      </dd>
                      <dt className="text-adobe-muted">
                        {personalSubscription.status === 'PAUSED'
                          ? '정지일'
                          : '이용 기간'}
                      </dt>
                      <dd className="text-neutral-950 dark:text-neutral-100">
                        {isLegacyPerpetual
                          ? '만료 없음'
                          : `~ ${fmtDate(personalSubscription.currentPeriodEnd)}`}
                      </dd>
                    </dl>

                    {isLegacy && (
                      <p className="mt-3 rounded-lg bg-black/[0.03] dark:bg-white/10 px-3 py-2 text-xs text-neutral-700 dark:text-neutral-300">
                        {isLegacyPerpetual
                          ? '기본 기능을 사용할 수 있습니다. 자원 다운로드·최신 판 기능은 구독이 필요합니다.'
                          : `${fmtDate(personalSubscription.currentPeriodEnd)}까지 이용할 수 있습니다. 이후에는 신 상품 구매가 필요합니다.`}{' '}
                        <Link
                          to={PATHS.STORE}
                          onClick={onClose}
                          className="font-medium underline"
                        >
                          상품 보기
                        </Link>
                      </p>
                    )}

                    {needsRecovery && (
                      <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                        {personalSubscription.status === 'PAUSED'
                          ? '결제가 지연되어 서비스가 일시정지되었습니다. 재활성화하면 즉시 복구됩니다.'
                          : '최근 결제가 실패했습니다. 재결제하거나 결제수단을 변경해 주세요.'}
                      </p>
                    )}

                    {pausedByOrg && (
                      <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                        소속 조직 상태로 인해 정지되었습니다. 조직 관리자에게
                        문의해주세요.
                      </p>
                    )}

                    {cancelPending && (
                      <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                        {annualPending
                          ? `중도해지 신청이 접수되었습니다. 영업일 3일 이내 ${annual.refundAmount.toLocaleString('ko-KR')}원이 환불되고 이용이 종료됩니다.`
                          : pendingReview
                            ? '해지 신청이 접수되어 심사 중입니다(영업일 기준 최대 7일). 승인 시 정산 후 해지됩니다.'
                            : `${DATE_MD.format(new Date(personalSubscription.currentPeriodEnd))}에 해지될 예정입니다. 그 전까지는 계속 이용할 수 있어요.`}
                      </p>
                    )}

                    {/*
                      청약철회 불가 사유는 **버튼을 숨기지 말고** 보여준다.
                      버튼이 없으면 "환불 기능이 원래 없다"고 읽혀 CS 로 온다.
                    */}
                    {withdrawal && !withdrawal.eligible && withdrawal.usedBlocks && (
                      <p className="mt-3 rounded-lg bg-black/[0.03] dark:bg-white/10 px-3 py-2 text-xs text-neutral-700 dark:text-neutral-300">
                        {/* 활성화 일시가 없으면 날짜를 생략한다 — '-' 를 찍으면 문장이 깨진다. */}
                        {withdrawal.activatedAt &&
                          `${fmtDate(withdrawal.activatedAt)} `}
                        {withdrawal.activatedDeviceName &&
                          `(${withdrawal.activatedDeviceName}) `}
                        라이선스 활성화로 청약철회가 제한됩니다.
                      </p>
                    )}

                    {!isCouponGrant && !isLegacy && (
                    <div className="mt-3 flex flex-wrap justify-end gap-2">
                      {/*
                        청약철회(§6.1).
                        - 가능하면 활성 버튼.
                        - 활성화로 막힌 경우에만 **비활성 + 사유**로 남긴다. 버튼을
                          없애면 "환불 기능이 원래 없다"고 오해해 CS 로 온다.
                        - 그 밖의 사유(기간 경과·기업·환불액 없음)는 청약철회가 애초에
                          할 일이 아니다. 렌더하지 않고 기존 '해지 신청'이 그 역할을 한다.
                      */}
                      {showWithdrawalButton && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!withdrawal?.eligible || lifecycleBusy}
                          onClick={() => setWithdrawalOpen(true)}
                        >
                          청약철회(전액 환불)
                        </Button>
                      )}
                      {/* 연간 중도해지 — 주기말 해지 예약 중에도 열어 둔다(남은 기간 환불을 원할 수 있다). */}
                      {showAnnualButton && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={lifecycleBusy}
                          onClick={() => setAnnualOpen(true)}
                        >
                          중도해지(환불)
                        </Button>
                      )}
                      {cancelPending ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={lifecycleBusy}
                          onClick={() =>
                            // mutate 는 reject 하지 않는다 — unhandled rejection 이 원천 불가.
                            // 실패 사유는 전역 mutation 에러 핸들러가 alert 로 알린다.
                            revokeCancellation.mutate({
                              id: personalSubscription.id,
                            })
                          }
                        >
                          구독해지 취소
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCancelOpen(true)}
                        >
                          해지 신청
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => void changeCard(personalSubscription.id)}
                      >
                        결제수단 변경
                      </Button>
                      {needsRecovery && (
                        <Link
                          to={PATHS.subscriptionReactivate(
                            personalSubscription.id,
                          )}
                          onClick={onClose}
                        >
                          <Button variant="primary" size="sm">
                            재활성화
                          </Button>
                        </Link>
                      )}
                    </div>
                    )}
                  </div>
                );
              })()}
            </section>
          )}

          {/* 라이선스 없음 (구독만 있는 경우) */}
          {licenses.length === 0 && (
            <p className="rounded-xl border border-dashed border-black/[0.12] p-4 text-center text-adobe-muted">
              발급된 라이선스가 없습니다.
            </p>
          )}

          {/* 개인 라이선스 */}
          {personalLicenses.length > 0 && (
            <section>
              <SectionTitle>개인 라이선스 ({personalLicenses.length})</SectionTitle>
              <ul className="space-y-2">
                {personalLicenses.map((l) => (
                  <LicenseCard
                    key={l.id}
                    license={l}
                    copied={copiedId === l.id}
                    onCopy={() => void copyKey(l)}
                  />
                ))}
              </ul>
            </section>
          )}

          {/* 기업 라이선스 (조직에서 나에게 배정된 것) */}
          {enterpriseLicenses.length > 0 && (
            <section>
              <SectionTitle>
                기업 라이선스 ({enterpriseLicenses.length})
              </SectionTitle>
              <div className="space-y-4">
                {enterpriseByOrg.map((group, gi) => (
                  <div key={gi}>
                    <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-neutral-600 dark:text-neutral-400">
                      <BuildingIcon />
                      {group.name}
                    </p>
                    <ul className="space-y-2">
                      {group.items.map((l) => (
                        <LicenseCard
                          key={l.id}
                          license={l}
                          copied={copiedId === l.id}
                          onCopy={() => void copyKey(l)}
                        />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-adobe-muted">
                기업 라이선스의 배정·관리는{' '}
                <Link
                  to={PATHS.BUSINESS}
                  onClick={onClose}
                  className="font-medium underline"
                >
                  기업 콘솔
                </Link>
                에서 이루어집니다.
              </p>
            </section>
          )}
        </div>
      )}
    </Modal>

    {/* 개인 구독 해지 신청 다이얼로그. 약정 여부는 다이얼로그가 프리뷰로 분기한다. */}
    <CancelSubscriptionDialog
      open={cancelOpen}
      subscriptionId={personalSubscription?.id ?? null}
      isCommitment={false}
      onClose={() => setCancelOpen(false)}
      onDone={onClose}
    />

    {/* 연간 중도해지 — 신청만 접수한다. 환불·종료는 운영자 승인 때 실행된다. */}
    <AnnualTerminationDialog
      open={annualOpen}
      preview={annual ?? null}
      planName={personalSubscription?.plan?.name ?? '개인 구독'}
      busy={requestAnnualTermination.isPending}
      onClose={() => setAnnualOpen(false)}
      onConfirm={() => {
        if (!annualCandidate) return;
        // mutate 는 reject 하지 않는다 — 실패 사유는 전역 mutation 에러 핸들러가 알린다.
        requestAnnualTermination.mutate(
          { id: annualCandidate.id },
          { onSuccess: () => setAnnualOpen(false) },
        );
      }}
    />

    {/* 청약철회 — 되돌릴 수 없으므로 환불액·즉시 종료를 확인 화면에 명시한다. */}
    <WithdrawalDialog
      open={withdrawalOpen}
      preview={withdrawal ?? null}
      planName={personalSubscription?.plan?.name ?? '개인 구독'}
      busy={requestWithdrawal.isPending}
      onClose={() => setWithdrawalOpen(false)}
      onConfirm={() => {
        if (!withdrawalCandidate) return;
        // mutate 는 reject 하지 않는다 — 실패 사유는 전역 mutation 에러 핸들러가 알린다.
        requestWithdrawal.mutate(
          { id: withdrawalCandidate.id },
          {
            onSuccess: () => {
              setWithdrawalOpen(false);
              onClose();
            },
          },
        );
      }}
    />
    </>
  );
}

// ── 하위 컴포넌트 ──────────────────────────────────────────
function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-adobe-muted">
      {children}
    </h3>
  );
}

function LicenseCard({
  license: l,
  copied,
  onCopy,
}: {
  license: MyLicense;
  copied: boolean;
  onCopy: () => void;
}) {
  const statusClassName =
    l.status === 'ACTIVE'
      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
      : 'bg-black/[0.04] text-neutral-700 dark:bg-white/10 dark:text-neutral-300';

  return (
    <li className="rounded-xl border border-black/[0.08] dark:border-white/10 p-3.5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          {/* 상품 단위(§6) — 한 플랜에 여러 상품이 있으면 상품명이 식별의 기준이 된다 */}
          <span className="block truncate font-medium text-neutral-950 dark:text-neutral-100">
            {l.productName}
            {specialSegmentLabel(l.licenseSegment) ? (
              <span className="ml-1.5 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-bold text-neutral-700 dark:bg-white/10 dark:text-neutral-200">
                {specialSegmentLabel(l.licenseSegment)}
              </span>
            ) : null}
          </span>
          {l.subscription?.plan?.name ? (
            <span className="block truncate text-xs text-adobe-muted">
              {l.subscription.plan.name}
            </span>
          ) : null}
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusClassName}`}
        >
          {LICENSE_STATUS_LABEL[l.status] ?? l.status}
        </span>
      </div>

      {/*
        키는 데스크탑 최초 실행 시 발급된다(lazy). 미발급 상태에서 빈 코드블록만
        보이면 "라이선스가 잘못됐다"고 오해하므로, 안내 문구로 대체한다.
      */}
      {l.licenseKey ? (
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 truncate rounded-md bg-black/[0.03] dark:bg-white/10 px-2.5 py-1.5 font-mono text-[13px] text-neutral-800 dark:text-neutral-200">
            {l.licenseKey}
          </code>
          <Button variant="ghost" size="sm" onClick={onCopy}>
            {copied ? '복사됨' : '복사'}
          </Button>
        </div>
      ) : (
        <p className="mt-2 rounded-md bg-black/[0.03] dark:bg-white/10 px-2.5 py-1.5 text-xs text-adobe-muted">
          데스크탑 앱에 로그인하면 라이선스 키가 발급됩니다.
        </p>
      )}

      <p className="mt-1.5 text-xs text-adobe-muted">
        {isPerpetualSegment(l.licenseSegment)
          ? '유효기간 만료 없음'
          : `유효기간 ~ ${fmtDate(l.validUntil)}`}
      </p>
    </li>
  );
}

function BuildingIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        d="M2.5 14V3.5A1 1 0 013.5 2.5h5A1 1 0 019.5 3.5V14M9.5 6.5h3a1 1 0 011 1V14M1.5 14h13"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path
        d="M4.5 5h1.5M4.5 7.5h1.5M4.5 10h1.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}
